import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

export interface UseTableHeightOptions {
  marginHeight?: number;
  debounceMs?: number;
  heightChangeThreshold?: number;
}

export interface UseTableHeightReturn {
  refContainer: React.RefObject<HTMLDivElement>;
  listHeight: number;
}

/**
 * Custom hook to handle table height calculation with ResizeObserver
 * Provides debounced height updates and fallback for older browsers
 */
export const useTableHeight = ({
  marginHeight = 75,
  debounceMs = 16,
  heightChangeThreshold = 5,
}: UseTableHeightOptions = {}): UseTableHeightReturn => {
  const refContainer = useRef<HTMLDivElement>(null);
  const oldHeight = useRef(0);
  const isUpdating = useRef(false);
  const [listHeight, setListHeight] = useState(0);

  // Calculate height with threshold check
  const calculateHeight = useCallback(() => {
    if (refContainer.current && !isUpdating.current) {
      const newHeight = refContainer.current.getBoundingClientRect().height;

      // Only update if height actually changed significantly
      if (Math.abs(newHeight - oldHeight.current) > heightChangeThreshold) {
        isUpdating.current = true; // Prevent infinite loop
        setListHeight(newHeight - marginHeight);
        oldHeight.current = newHeight;

        // Reset the flag after a short delay
        setTimeout(() => {
          isUpdating.current = false;
        }, 100);
      }
    }
  }, [marginHeight, heightChangeThreshold]);

  // Create debounced version of calculateHeight
  const debouncedCalculateHeight = useDebouncedCallback(
    calculateHeight,
    debounceMs
  );

  // Initial height calculation (immediate, not debounced)
  const updateInitialHeight = useCallback(() => {
    if (refContainer.current && !isUpdating.current) {
      const currentHeight = refContainer.current.getBoundingClientRect().height;
      setListHeight(currentHeight - marginHeight);
      oldHeight.current = currentHeight;
    }
  }, [marginHeight]);

  useEffect(() => {
    // Set initial height immediately
    updateInitialHeight();

    // Check ResizeObserver support to prevent crashes
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(debouncedCalculateHeight);
      if (refContainer.current) {
        resizeObserver.observe(refContainer.current);
      }
    } else {
      // Fallback: Use window resize event for older browsers
      window.addEventListener('resize', debouncedCalculateHeight);
    }

    // Cleanup
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', debouncedCalculateHeight);
      }
    };
  }, [updateInitialHeight, debouncedCalculateHeight]);

  return {
    refContainer,
    listHeight,
  };
};
